import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../api';
import styles from './Persona.module.css';
import { personIcon } from '../assets/icons';
import EditProfileModal from './utils/EditProfileModal';
import EditableField from './utils/EditableField';
import { useEditableField } from '../hooks/useEditableField';
import modalStyles from './utils/EditProfileModal.module.css';
import { useAuth } from '../context/AuthContext';
import type { PersonaType } from '../api/personas';

// Fix for useParams type
type PersonaParams = {
  personaId: string;
};

function Persona(): JSX.Element {
  const navigate = useNavigate();
  const { personaId } = useParams<keyof PersonaParams>();
  const { user } = useAuth();
  const [persona, setPersona] = useState<PersonaType | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [isMakingDefault, setIsMakingDefault] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchPersona() {
      try {
        const fetchedPersona = await api.personas.get(personaId as string);
        setPersona(fetchedPersona);
      } catch (error) {
        console.error('Failed to fetch persona:', error);
        setError('Failed to fetch persona');
      } finally {
        setLoading(false);
      }
    }

    fetchPersona();
  }, [personaId]);

  const displayName = useEditableField({
    initialValue: persona?.display_name,
    fieldName: 'display name',
    onUpdate: async (newValue: string) => {
      if (!persona) return null;
      const updated = await persona.update({ display_name: newValue.trim() });
      setPersona(updated);
      return updated;
    },
    validate: (value: string) => {
      if (!value.trim()) return 'Name cannot be empty';
      return undefined;
    }
  });

  const description = useEditableField({
    initialValue: persona?.description,
    fieldName: 'description',
    onUpdate: async (newValue: string) => {
      if (!persona) return null;
      const updated = await persona.update({ description: newValue.trim() });
      setPersona(updated);
      return updated;
    },
    validate: (value: string) => {
      if (!value.trim()) return 'Description cannot be empty';
      return undefined;
    }
  });

  const handleIconChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !persona) return;

    setIsUpdating(true);
    try {
      // Create a temporary URL for immediate UI update
      const tempUrl = URL.createObjectURL(file);
      // Create a temporary display update without modifying the actual Persona instance
      const tempPersona = { ...persona, icon: tempUrl };
      // @ts-ignore - This is a temporary workaround for the TypeScript error
      setPersona(tempPersona);

      const updated = await persona.update({ icon: file });
      setPersona(updated);
      URL.revokeObjectURL(tempUrl);
    } catch (error) {
      console.error('Failed to update persona icon:', error);
      setError('Failed to update icon');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleMakeDefault = async () => {
    if (isMakingDefault || !persona) return;
    
    setIsMakingDefault(true);
    try {
      await persona.makeDefault();
      // Create a temporary display update without modifying the actual Persona instance
      const tempPersona = { ...persona, is_default: true };
      // @ts-ignore - This is a temporary workaround for the TypeScript error
      setPersona(tempPersona);
    } catch (error) {
      console.error('Failed to make persona default:', error);
      setError('Failed to make persona default');
    } finally {
      setIsMakingDefault(false);
    }
  };

  if (loading) {
    return <div className={styles.loading}>Loading...</div>;
  }

  if (!persona) {
    return <div className={styles.error}>Persona not found</div>;
  }

  const renderDisplayName = (value: string) => (
    <h1 className={styles.displayName}>{value}</h1>
  );

  const renderDescription = (value: string) => (
    <p className={styles.description}>{value || "This persona hasn't written their description yet."}</p>
  );

  return (
    <div className={styles.wrapper}>
      <div 
        className={styles.backgroundImage} 
        style={{ 
          backgroundImage: `url(${isUpdating ? persona.icon : (persona.icon || '/hangjam.png')})`,
          transition: 'filter 0.3s ease'
        }} 
      />
      <div className={styles.container}>
        <button 
          className={styles.backButton}
          onClick={() => navigate(`/users/${user?.username}`)}
        >
          ‹
        </button>
        
        <div className={styles.iconWrapper}>
          <img 
            src={persona.icon || personIcon} 
            alt={persona.display_name} 
            className={styles.personaIcon}
          />
          <label className={`${styles.editIconButton} ${isUpdating ? styles.updating : ''}`} htmlFor="icon-upload">
            {isUpdating ? (
              <div className={styles.spinner} />
            ) : (
              <span className={styles.editIconSymbol}>✎</span>
            )}
            <input
              type="file"
              id="icon-upload"
              accept="image/*"
              onChange={handleIconChange}
              style={{ display: 'none' }}
              disabled={isUpdating}
            />
          </label>
        </div>

        <div className={styles.nameContainer}>
          <EditableField
            value={persona.display_name}
            onEdit={displayName.startEdit}
            isEditing={displayName.isEditing}
            isLoading={displayName.isLoading}
            error={displayName.error}
            renderDisplay={renderDisplayName}
            containerClassName={styles.displayNameContainer}
          />
        </div>

        <div className={styles.descriptionContainer}>
          <EditableField
            value={persona.description}
            onEdit={description.startEdit}
            isEditing={description.isEditing}
            isLoading={description.isLoading}
            error={description.error}
            renderDisplay={renderDescription}
            containerClassName={styles.descriptionContainer}
          />
        </div>

        {persona.is_default ? (
          <div className={styles.defaultBadge}>
            <span>✓</span>
            <span>Default Persona</span>
          </div>
        ) : (
          <button
            className={styles.makeDefaultButton}
            onClick={handleMakeDefault}
            disabled={isMakingDefault}
          >
            {isMakingDefault ? (
              <div className={styles.spinner} />
            ) : (
              <>
                <span>☆</span>
                <span>Make Default</span>
              </>
            )}
          </button>
        )}
      </div>

      <EditProfileModal
        title="Edit Name"
        isOpen={displayName.isEditing}
        onClose={() => displayName.setIsEditing(false)}
        onSubmit={displayName.handleSubmit}
        error={displayName.error}
      >
        <input
          type="text"
          value={displayName.value}
          onChange={(e) => displayName.setValue(e.target.value)}
          placeholder="Enter new name"
          className={modalStyles.nameInput}
          disabled={displayName.isLoading}
        />
      </EditProfileModal>

      <EditProfileModal
        title="Edit Description"
        isOpen={description.isEditing}
        onClose={() => description.setIsEditing(false)}
        onSubmit={description.handleSubmit}
        error={description.error}
      >
        <textarea
          value={description.value}
          onChange={(e) => description.setValue(e.target.value)}
          placeholder="Enter new description"
          className={modalStyles.textArea}
          rows={4}
          disabled={description.isLoading}
        />
      </EditProfileModal>
    </div>
  );
}

export default Persona; 